import type { IAdViewManaged } from "@finn-no/adview-provider-frontend-types";
import { useLingui } from "@lingui/react";
import { getValue } from "@mobility-item-page/utilities";
import { type Placement, Tooltip } from "./Tooltip.tsx";

type Size = "m" | "l";

/** Type scale of the value, keyed by {@link Props.size} */
const VALUE_CLASS: Record<Size, string> = {
  m: "t4",
  l: "t3",
};

type Props = {
  field: string;
  value?: string | IAdViewManaged | number | null | boolean;
  link?: {
    href?: string;
    text: string;
  };
  tooltip?: string;
  /** Where the tooltip opens relative to the info icon. Defaults to "right". */
  tooltipPlacement?: Placement;
  /**
   * Called the first time the tooltip opens, however it is opened — hover, tap or keyboard focus.
   * Opt-in, so the many pairs that are not tracked stay free of the pulse context.
   */
  onTooltipOpen?: () => void;
  suppressHydrationWarning?: boolean;
  sup?: string;
  className?: string;
  /** Type scale of the value. Defaults to "m"; "l" is used in the EV info cards. */
  size?: Size;
};

const FieldValuePair = ({
  field: rawField,
  value: rawValue,
  link,
  tooltip,
  tooltipPlacement = "right",
  onTooltipOpen,
  suppressHydrationWarning,
  sup,
  className,
  size = "m",
}: Props) => {
  const { i18n } = useLingui();

  const yesText = i18n.t({
    id: "itemPageLayout.keyInfo.yes",
    message: "Yes",
    comment: "Field for 'yes' in ad specification",
  });
  const noText = i18n.t({
    id: "itemPageLayout.keyInfo.no",
    message: "No",
    comment: "Field for 'no' in ad specification",
  });

  let value = typeof rawValue === "object" ? getValue(rawValue) : rawValue;

  if (typeof rawValue === "boolean") {
    value = rawValue ? yesText : noText;
  }
  if ((value == null || value === "") && !link) {
    return null;
  }

  const words = rawField.split(" ");
  const field = tooltip ? (
    <>
      {words.slice(0, -1).join(" ")}
      {words.length > 1 && " "}
      <span className="whitespace-nowrap">
        {words.at(-1)}
        <Tooltip tooltip={tooltip} label={rawField} placement={tooltipPlacement} onOpen={onTooltipOpen} />
      </span>
    </>
  ) : (
    rawField
  );

  const getContent = () => {
    if (link?.href && value) {
      return (
        <>
          {value}{" "}
          <a href={link.href} target="_blank" rel="noopener">
            ({link.text})
          </a>
        </>
      );
    }
    if (!link && value != null) {
      return value;
    }
    if (link && !value) {
      return (
        <a className={VALUE_CLASS[size]} href={link.href} target="_blank" rel="noopener">
          {link.text}
        </a>
      );
    }
    return null;
  };

  return (
    <div className={`pb-16 md:pb-20 ${className ?? ""}`} style={{ breakInside: "avoid-column" }}>
      <dt className="capitalize&::first-letter m-0 s-text-subtle">{field}</dt>
      <dd className={`${VALUE_CLASS[size]} m-0`} suppressHydrationWarning={suppressHydrationWarning}>
        {getContent()}
        {sup && <sup>{sup}</sup>}
      </dd>
    </div>
  );
};

export default FieldValuePair;
